import type { Database } from 'node-sqlite3-wasm';
import type { SymbolImportBinding, SymbolTag } from '../../types.js';
import { deleteSymbolContentIfUnreferenced, upsertSymbolContentIndex } from '../storage/symbol-content-index.js';
import { getEffectiveContentHash } from '../storage/effective-content.js';
import {
  clearParentFileMap,
  clearParentFileMapBatch,
  getParentFileMapHash,
  removeParentFileMapEntry,
  setParentFileMapEntry,
} from '../storage/parent-file-map.js';
import type { SymbolIndexParentContext } from '../storage/parent-context.js';
import { hashSymbolFileContent } from '../persistence/symbol-file-content-hash.js';

function gcReleasedHashes(db: Database, hashes: Iterable<string | null | undefined>): void {
  for (const hash of hashes) {
    if (hash != null) deleteSymbolContentIfUnreferenced(db, hash);
  }
}

function persistFileSymbolData(
  db: Database,
  ctx: SymbolIndexParentContext,
  filePath: string,
  contentHash: string,
  tags: SymbolTag[],
  imports: SymbolImportBinding[],
): void {
  upsertSymbolContentIndex(db, contentHash, filePath, tags, imports);

  if (ctx.kind === 'overlay' && ctx.baseParentId != null) {
    const baseHash = getParentFileMapHash(db, ctx.baseParentId, filePath);
    if (baseHash === contentHash) {
      const released = removeParentFileMapEntry(db, ctx.parentId, filePath);
      gcReleasedHashes(db, [released]);
      return;
    }
  }

  const released = setParentFileMapEntry(db, ctx.parentId, filePath, contentHash);
  gcReleasedHashes(db, [released]);
}

export function persistSymbolIndexFileData(
  db: Database,
  ctx: SymbolIndexParentContext,
  filePath: string,
  source: string | null,
  tags: SymbolTag[],
  imports: SymbolImportBinding[],
): void {
  const contentHash = hashSymbolFileContent(source);
  persistFileSymbolData(db, ctx, filePath, contentHash, tags, imports);
}

export function removeSymbolIndexFileData(db: Database, ctx: SymbolIndexParentContext, filePath: string): void {
  if (ctx.kind === 'overlay') {
    const released = setParentFileMapEntry(db, ctx.parentId, filePath, null);
    gcReleasedHashes(db, [released]);
    return;
  }

  const released = removeParentFileMapEntry(db, ctx.parentId, filePath);
  gcReleasedHashes(db, [released]);
}

export function clearSymbolIndexParentData(db: Database, ctx: SymbolIndexParentContext): void {
  const released = clearParentFileMap(db, ctx.parentId);
  gcReleasedHashes(db, released);
}

export function clearSymbolIndexParentDataBatch(
  db: Database,
  ctx: SymbolIndexParentContext,
  limit: number,
): boolean {
  const { released, remaining } = clearParentFileMapBatch(db, ctx.parentId, limit);
  gcReleasedHashes(db, released);
  return remaining;
}

export function parentHasIndexedFile(
  db: Database,
  ctx: SymbolIndexParentContext,
  filePath: string,
): boolean {
  return getEffectiveContentHash(db, ctx, filePath) != null;
}
